/**
 * Confirms dropping a partner from a dispatched assignment.
 *
 * Cancelling is a real action — the partner request is cancelled in Backoffice
 * and the partner is emailed if they had been told about it — so it asks for a
 * reason, which is stored with who and when. It states what stays (the
 * assignment, its files and its fee) and that a replacement is a separate step.
 */

import { useEffect, useState } from 'react';

import { Button, Group, List, Modal, Stack, Text, Textarea } from '@mantine/core';

import { isValidReason, MIN_REASON_LENGTH } from '@/types/commercial-change';

interface CancelAssignmentDialogProps {
  opened: boolean;
  /** Who is being dropped, for the heading. */
  partnerName: string;
  submitting?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export function CancelAssignmentDialog({
  opened,
  partnerName,
  submitting = false,
  onConfirm,
  onCancel,
}: Readonly<CancelAssignmentDialogProps>) {
  const [reason, setReason] = useState('');

  // Each time the dialog opens it starts blank — a reason is for one cancellation.
  useEffect(() => {
    if (opened) {
      setReason('');
    }
  }, [opened]);

  const valid = isValidReason(reason);
  const typed = reason.trim().length;

  return (
    <Modal
      opened={opened}
      onClose={submitting ? () => undefined : onCancel}
      title={`Cancel ${partnerName}?`}
      data-testid="cancel-assignment-dialog"
    >
      <Stack gap="sm">
        <List size="sm" spacing={4}>
          <List.Item>
            The partner request is cancelled in Backoffice, and the partner is emailed if they had
            been told about it.
          </List.Item>
          <List.Item>The assignment, its files and its fee stay on the record.</List.Item>
          <List.Item>
            This does not add a replacement — add another partner as a separate step.
          </List.Item>
        </List>

        <Textarea
          label="Reason"
          placeholder="e.g. Partner has gone quiet and the client needs this now"
          minRows={3}
          autosize
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          description={
            valid
              ? undefined
              : `At least ${MIN_REASON_LENGTH} characters (${typed}/${MIN_REASON_LENGTH})`
          }
          data-testid="cancel-assignment-reason"
          data-autofocus
        />
        <Text size="xs" c="dimmed">
          Recorded with your name and the time.
        </Text>

        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel} disabled={submitting}>
            Keep partner
          </Button>
          <Button
            color="red"
            onClick={() => void onConfirm(reason.trim())}
            disabled={!valid}
            loading={submitting}
            data-testid="cancel-assignment-confirm"
          >
            Cancel partner
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
